import {Children, useEffect, useMemo, useState, type ReactNode} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Globe,
  Loader2,
  Search,
  XCircle,
} from 'lucide-react';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {DetailHeader} from '@internal/components/DetailHeader';
import {EnrollmentSitesTabs} from '@internal/pages/enrollment/EnrollmentSitesTabs';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Bar, BarChart, CartesianGrid, XAxis, YAxis} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@common-web/components/ui/chart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@common-web/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';

type ActivityView = RouterOutput['internal']['enrollmentSite']['activity'];
type Range = ActivityView['range'];
type RowStatus = ActivityView['rows'][number]['status'];

const RANGES: {value: Range; label: string}[] = [
  {value: '24h', label: '24 hours'},
  {value: '7d', label: '7 days'},
  {value: '30d', label: '30 days'},
];
const RANGE_LABEL: Record<Range, string> = {
  '24h': 'last 24 hours',
  '7d': 'last 7 days',
  '30d': 'last 30 days',
};
const STATUS_LABEL: Record<RowStatus, string> = {
  enrolled: 'Enrolled',
  duplicate: 'Duplicate',
  failed: 'Failed',
};
const STATUS_TONE: Record<RowStatus, string> = {
  enrolled: 'bg-emerald-50 text-emerald-700',
  duplicate: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700',
};
const ALL = '__all';
const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

const formatTime = (value: Date | string) =>
  new Date(value).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const isRange = (value: string | null): value is Range => RANGES.some((r) => r.value === value);

export function EnrollmentActivityPage() {
  const [params, setParams] = useSearchParams();
  const range: Range = isRange(params.get('range')) ? (params.get('range') as Range) : '24h';
  const [site, setSite] = useState(ALL);
  const [status, setStatus] = useState<string>(params.get('status') ?? ALL);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const {data, isLoading, isError, refetch} = trpc.internal.enrollmentSite.activity.useQuery({
    range,
  });

  const siteOptions = useMemo(
    () =>
      [...new Map((data?.rows ?? []).map((r) => [r.groupSlug, r.siteName])).entries()].sort(
        (a, b) => a[1].localeCompare(b[1]),
      ),
    [data],
  );
  const rows = (data?.rows ?? []).filter(
    (r) =>
      (site === ALL || r.groupSlug === site) &&
      (status === ALL || r.status === status) &&
      (!query.trim() || r.memberName.toLowerCase().includes(query.trim().toLowerCase())),
  );
  // Back to the first page whenever what's being listed changes.
  useEffect(() => setPage(1), [range, site, status, query, pageSize]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className='space-y-4 px-6 py-6'>
      <DetailHeader
        icon={<Globe className='h-5 w-5' />}
        title='Enrollment Sites'
        subtitle='Build, publish, and track every employer’s enrollment site.'
      />

      <EnrollmentSitesTabs
        right={
          <div
            className='inline-flex rounded-md bg-muted p-0.5'
            role='group'
            aria-label='Time range'
          >
            {RANGES.map((r) => (
              <button
                key={r.value}
                type='button'
                aria-pressed={range === r.value}
                onClick={() => setParams((p) => (p.set('range', r.value), p), {replace: true})}
                className={cn(
                  'rounded px-2.5 py-0.5 text-xs text-muted-foreground',
                  range === r.value && 'bg-white font-medium text-foreground shadow-sm',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      {isLoading ? (
        <div className='py-16 text-center'>
          <Loader2 className='mx-auto h-5 w-5 animate-spin text-muted-foreground' />
        </div>
      ) : isError || !data ? (
        <div className='flex flex-col items-center gap-3 py-16 text-sm text-muted-foreground'>
          Couldn’t load enrollment activity.
          <Button variant='outline' size='sm' onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
            <SummaryCard
              icon={<CheckCircle2 className='h-4 w-4' />}
              label='Enrolled'
              value={data.summary.enrolled}
              tone='text-emerald-600'
              note={`${data.summary.sitesWithEnrollments} site${data.summary.sitesWithEnrollments === 1 ? '' : 's'}`}
            />
            <SummaryCard
              icon={<XCircle className='h-4 w-4' />}
              label='Failed'
              value={data.summary.failed}
              tone='text-red-600'
              note={data.summary.failed ? `${data.summary.failedOpen} open` : undefined}
            />
            <SummaryCard
              icon={<Copy className='h-4 w-4' />}
              label='Duplicates blocked'
              value={data.summary.duplicates}
              tone='text-amber-600'
              note={
                data.summary.duplicatesWithChanges
                  ? `${data.summary.duplicatesWithChanges} tried to change plans`
                  : undefined
              }
            />
            <SummaryCard
              icon={<Activity className='h-4 w-4' />}
              label='All attempts'
              value={data.summary.attempts}
              tone='text-slate-500'
            />
          </div>

          <div className='grid gap-3 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]'>
            <NeedsAttention attention={data.attention} rangeLabel={RANGE_LABEL[range]} />
            <div className='grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-1 xl:content-start'>
              <Panel title='Enrolled by site' meta={RANGE_LABEL[range]}>
                <BySite bySite={data.bySite} />
              </Panel>
              <Panel title='Enrollments over time' meta={RANGE_LABEL[range]}>
                <TrendChart trend={data.trend} />
              </Panel>
            </div>
          </div>

          <Panel
            title={`Submissions · ${RANGE_LABEL[range]}`}
            meta={
              <div className='flex flex-wrap items-center gap-2'>
                <Select value={site} onValueChange={setSite}>
                  <SelectTrigger className='h-8 w-48' aria-label='Site'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All sites</SelectItem>
                    {siteOptions.map(([slug, name]) => (
                      <SelectItem key={slug} value={slug}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className='h-8 w-36' aria-label='Status'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All statuses</SelectItem>
                    {(Object.keys(STATUS_LABEL) as RowStatus[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className='relative'>
                  <Search className='absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder='Search name'
                    aria-label='Search name'
                    className='h-8 w-44 pl-8'
                  />
                </div>
              </div>
            }
          >
            <div className='overflow-x-auto rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time (CT)</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Plans</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className='py-8 text-center text-muted-foreground'>
                        No submissions match.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className='whitespace-nowrap tabular-nums'>
                          {formatTime(r.at)}
                        </TableCell>
                        <TableCell>
                          <Link to={`/enrollment-sites/${r.groupSlug}`} className='hover:underline'>
                            {r.siteName}
                          </Link>
                          {r.location && (
                            <div className='text-xs text-muted-foreground'>{r.location}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.memberName}
                          {r.attempts > 1 && (
                            <span className='ml-1 text-xs text-muted-foreground'>
                              ×{r.attempts}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className='text-xs text-muted-foreground'>
                          {r.plans.join(', ') || '—'}
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-xs font-medium',
                              STATUS_TONE[r.status],
                            )}
                          >
                            {STATUS_LABEL[r.status]}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <div className='flex items-center justify-between border-t px-4 py-2 text-sm text-muted-foreground'>
                <span>
                  {rows.length === 0
                    ? 'No results'
                    : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, rows.length)} of ${rows.length}`}
                </span>
                <div className='flex items-center gap-3'>
                  <div className='flex items-center gap-2'>
                    <span>Rows per page</span>
                    <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                      <SelectTrigger className='h-8 w-[70px]' aria-label='Rows per page'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant='outline'
                    size='sm'
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label='Previous page'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label='Next page'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                </div>
              </div>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

// Same two-line shape as the Sites tab's stat cards; the note sits beside the number.
function SummaryCard({
  icon,
  label,
  value,
  tone,
  note,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  tone: string;
  note?: string;
}) {
  return (
    <div className='rounded-lg border bg-white p-4'>
      <div className={cn('flex items-center gap-1.5 text-xs font-medium', tone)}>
        {icon}
        {label}
      </div>
      <div className='mt-1 flex items-baseline gap-2'>
        <span className='text-2xl font-semibold tabular-nums'>{value.toLocaleString()}</span>
        {note && <span className='truncate text-xs text-muted-foreground'>{note}</span>}
      </div>
    </div>
  );
}

function Panel({title, meta, children}: {title: string; meta?: ReactNode; children: ReactNode}) {
  return (
    <section className='flex min-w-0 flex-col gap-3 rounded-lg border bg-white p-4'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <h2 className='text-sm font-semibold'>{title}</h2>
        {typeof meta === 'string' ? (
          <span className='text-xs text-muted-foreground'>{meta}</span>
        ) : (
          meta
        )}
      </div>
      {children}
    </section>
  );
}

function NeedsAttention({
  attention,
  rangeLabel,
}: {
  attention: ActivityView['attention'];
  rangeLabel: string;
}) {
  const open = attention.failed.length + attention.planChanges.length;
  const empty = open + attention.resolved.length === 0;
  return (
    <Panel title='Needs attention' meta={empty ? rangeLabel : `${open} open · ${rangeLabel}`}>
      {empty ? (
        <p className='flex flex-1 items-center justify-center py-6 text-center text-sm text-muted-foreground'>
          Nothing needs attention in this range.
        </p>
      ) : (
        <div className='flex flex-col gap-5'>
          <AttentionGroup
            icon={<XCircle className='h-3.5 w-3.5' />}
            title='Failed'
            count={attention.failed.length}
            tone='text-red-600'
          >
            {attention.failed.map((f) => (
              <AttentionItem
                key={`${f.groupSlug}:${f.memberName}`}
                name={f.memberName}
                site={f.siteName}
                reason={f.reason}
                at={f.lastAt}
              >
                <div className='text-xs text-muted-foreground'>
                  {f.attempts} attempt{f.attempts === 1 ? '' : 's'}
                  {f.plans.length ? ` · ${f.plans.join(', ')}` : ''}
                </div>
                <ErrorText>{f.error}</ErrorText>
                <Link
                  to={`/enrollment-sites/${f.groupSlug}`}
                  className='text-xs text-primary hover:underline'
                >
                  Open {f.siteName} in builder
                </Link>
              </AttentionItem>
            ))}
          </AttentionGroup>

          <AttentionGroup
            icon={<Copy className='h-3.5 w-3.5' />}
            title='Duplicates blocked: plan change attempted'
            count={attention.planChanges.length}
            tone='text-amber-600'
          >
            {attention.planChanges.map((c) => (
              <AttentionItem
                key={c.id}
                name={c.memberName}
                site={c.siteName}
                reason={c.summary}
                at={c.at}
              >
                <div className='text-xs text-muted-foreground'>
                  Already enrolled {formatTime(c.originalAt)}. Tried to change:
                </div>
                <dl className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-sm'>
                  {c.changes.map((change) => (
                    <PlanChangeLine key={change.label} {...change} />
                  ))}
                </dl>
                <p className='text-xs text-amber-700'>
                  Blocked, so e123 still has the original enrollment. If this change is intended,
                  make it in e123.
                </p>
              </AttentionItem>
            ))}
          </AttentionGroup>

          <AttentionGroup
            icon={<CheckCircle2 className='h-3.5 w-3.5' />}
            title='Resolved'
            count={attention.resolved.length}
            tone='text-emerald-600'
          >
            {attention.resolved.map((f) => (
              <AttentionItem
                key={`${f.groupSlug}:${f.memberName}`}
                name={f.memberName}
                site={f.siteName}
                reason={f.resolvedAt ? `Enrolled ${formatTime(f.resolvedAt)}` : f.reason}
                at={f.lastAt}
                muted
              >
                <div className='text-xs'>
                  {f.attempts} failed attempt{f.attempts === 1 ? '' : 's'}
                  {f.plans.length ? ` · ${f.plans.join(', ')}` : ''}
                </div>
                <ErrorText>{f.error}</ErrorText>
              </AttentionItem>
            ))}
          </AttentionGroup>
        </div>
      )}
    </Panel>
  );
}

// Rows shown per group before "Show N more".
const GROUP_PREVIEW = 5;

function AttentionGroup({
  icon,
  title,
  count,
  tone,
  children,
}: {
  icon: ReactNode;
  title: string;
  count: number;
  tone: string;
  children: ReactNode;
}) {
  const [showAll, setShowAll] = useState(false);
  if (!count) return null;
  const items = Children.toArray(children);
  const hidden = items.length - GROUP_PREVIEW;
  return (
    <div className='flex flex-col'>
      <div
        className={cn(
          'flex items-center gap-1.5 border-b pb-1.5 text-xs font-semibold uppercase tracking-wide',
          tone,
        )}
      >
        {icon}
        {title}
        <span className='tabular-nums text-muted-foreground'>{count}</span>
      </div>
      <div className='divide-y'>{showAll ? items : items.slice(0, GROUP_PREVIEW)}</div>
      {hidden > 0 && (
        <button
          type='button'
          onClick={() => setShowAll((v) => !v)}
          className='self-start pt-1 text-xs text-primary hover:underline'
        >
          {showAll ? 'Show less' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
}

// Collapsed to one line (who, where, why, when); details open on click.
function AttentionItem({
  name,
  site,
  reason,
  at,
  muted,
  children,
}: {
  name: string;
  site: string;
  reason: string;
  at: Date | string;
  muted?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('py-2', muted && 'text-muted-foreground')}>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className='flex w-full items-center gap-2 rounded text-left text-sm hover:bg-muted/50'
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
        />
        <span className='shrink-0'>
          <span className='font-medium text-foreground'>{name}</span> · {site}
        </span>
        <span className='min-w-0 flex-1 truncate text-xs text-muted-foreground' title={reason}>
          {reason}
        </span>
        <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>
          {formatTime(at)}
        </span>
      </button>
      {open && <div className='mt-2 flex flex-col gap-1.5 pl-6'>{children}</div>}
    </div>
  );
}

function ErrorText({children}: {children: ReactNode}) {
  return (
    <p className='break-words rounded bg-muted px-2 py-1 font-mono text-xs text-foreground'>
      {children}
    </p>
  );
}

function PlanChangeLine({
  label,
  from,
  to,
}: {
  label: string;
  from: string | null;
  to: string | null;
}) {
  return (
    <>
      <dt className='text-muted-foreground'>{label}</dt>
      <dd>
        {from && to ? (
          <>
            <span className='text-muted-foreground line-through'>{from}</span> →{' '}
            <span className='font-medium text-emerald-700'>{to}</span>
          </>
        ) : to ? (
          <span className='font-medium text-emerald-700'>Added {to}</span>
        ) : (
          <span className='text-red-700'>Removed {from}</span>
        )}
      </dd>
    </>
  );
}

function BySite({bySite}: {bySite: ActivityView['bySite']}) {
  if (!bySite.length)
    return (
      <p className='py-4 text-center text-sm text-muted-foreground'>
        No enrollments in this range.
      </p>
    );
  const max = Math.max(...bySite.map((s) => s.enrolled));
  return (
    <div className='flex flex-col gap-2'>
      {bySite.map((s) => (
        <div
          key={s.groupSlug}
          className='grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)_2rem] items-center gap-2 text-sm'
        >
          <span className='truncate' title={s.siteName}>
            {s.siteName}
          </span>
          <span className='h-2.5 overflow-hidden rounded bg-muted'>
            <span
              className='block h-full rounded bg-primary'
              style={{width: `${(s.enrolled / max) * 100}%`}}
            />
          </span>
          <span className='text-right tabular-nums'>{s.enrolled}</span>
        </div>
      ))}
    </div>
  );
}

const TREND_CONFIG = {
  enrolled: {label: 'Enrolled', color: 'hsl(var(--primary))'},
} satisfies ChartConfig;

// A tick mark under every bar; labels only at fixed, regular points: every 6 hours on the
// hourly view, each day on 7 days, and every 7th day (ending today) on 30 days.
const HOURLY_LABELS = new Set(['12 AM', '6 AM', '12 PM', '6 PM']);
function trendLabels(trend: ActivityView['trend']): Set<string> {
  if (trend.length === 24) return HOURLY_LABELS;
  if (trend.length <= 7) return new Set(trend.map((b) => b.label));
  return new Set(trend.filter((_, i) => (trend.length - 1 - i) % 7 === 0).map((b) => b.label));
}

function TrendChart({trend}: {trend: ActivityView['trend']}) {
  const labels = trendLabels(trend);
  return (
    <ChartContainer
      config={TREND_CONFIG}
      className='aspect-auto h-32 w-full md:h-auto md:min-h-32 md:flex-1 xl:h-32 xl:flex-none'
    >
      <BarChart data={trend} margin={{top: 4, right: 16, left: -8, bottom: 0}}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey='label'
          ticks={trend.map((b) => b.label)}
          interval={0}
          tickSize={4}
          tickLine={false}
          // Draws its own mark: darker under a labelled bar, faint under the rest.
          tick={({x, y, payload}: {x: number; y: number; payload: {value: string}}) => {
            const labelled = labels.has(payload.value);
            return (
              <g>
                <line
                  x1={x}
                  x2={x}
                  y1={y - 6}
                  y2={y - 2}
                  stroke='hsl(var(--muted-foreground))'
                  strokeOpacity={labelled ? 0.9 : 0.35}
                />
                {labelled && (
                  <text
                    x={x}
                    y={y}
                    dy={10}
                    textAnchor='middle'
                    className='fill-muted-foreground text-xs'
                  >
                    {payload.value}
                  </text>
                )}
              </g>
            );
          }}
          axisLine={false}
        />
        <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={32} />
        <ChartTooltip cursor={{fill: 'hsl(var(--muted))'}} content={<ChartTooltipContent />} />
        <Bar dataKey='enrolled' fill='var(--color-enrolled)' radius={2} />
      </BarChart>
    </ChartContainer>
  );
}
